import React, { useState } from 'react';
import { Button, alpha, useTheme } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import BulkDeleteConfirmDialog from './BulkDeleteConfirmDialog';

export interface BulkDeletePillButtonProps {
  selectedCount: number;
  onDeleteSelected: () => Promise<void>;
  onDeleteByFilters: () => Promise<{ deletedCount: number }>;
  resourceLabel: string;
}

/**
 * Unified pill-style delete button matching Filters/Metadata pill buttons.
 * - No selection: "Delete" (subtle) → deletes all matching current filters
 * - With selection: "Delete (N)" (red accent) → deletes selected items
 */
const BulkDeletePillButton: React.FC<BulkDeletePillButtonProps> = ({
  selectedCount,
  onDeleteSelected,
  onDeleteByFilters,
  resourceLabel,
}) => {
  const translate = useTranslate();
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletedCount, setDeletedCount] = useState<number | undefined>(undefined);

  const hasSelection = selectedCount > 0;

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      if (hasSelection) {
        await onDeleteSelected();
        setDeletedCount(selectedCount);
      } else {
        const result = await onDeleteByFilters();
        setDeletedCount(result.deletedCount);
      }
    } catch {
      setDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setDeletedCount(undefined);
  };

  const label = hasSelection
    ? `${translate(`resources.${resourceLabel}.bulkDelete.deleteSelected`)} (${selectedCount})`
    : translate(`resources.${resourceLabel}.bulkDelete.deleteByFilters`);

  return (
    <>
      <Button
        onClick={() => setDialogOpen(true)}
        size="small"
        variant="outlined"
        startIcon={<DeleteIcon sx={{ fontSize: '16px !important' }} />}
        sx={{
          fontSize: '0.75rem',
          py: 0.4,
          px: 1.5,
          minHeight: '30px',
          lineHeight: 1.2,
          borderRadius: '16px',
          textTransform: 'none',
          fontWeight: 500,
          ...(hasSelection
            ? {
                borderColor: theme.palette.error.main,
                color: theme.palette.error.main,
                backgroundColor: alpha(theme.palette.error.main, 0.04),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  borderColor: theme.palette.error.dark,
                },
              }
            : {
                borderColor: alpha(theme.palette.text.secondary, 0.3),
                color: 'text.secondary',
                '&:hover': {
                  borderColor: theme.palette.error.main,
                  color: theme.palette.error.main,
                  backgroundColor: alpha(theme.palette.error.main, 0.04),
                },
              }),
        }}
      >
        {label}
      </Button>
      <BulkDeleteConfirmDialog
        open={dialogOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        count={selectedCount}
        resourceLabel={resourceLabel}
        isDeleting={isDeleting}
        deletedCount={deletedCount}
        byFilters={!hasSelection}
      />
    </>
  );
};

export default BulkDeletePillButton;
